import { Brain, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Practice Feedback Panel - Shows detailed feedback for practice mode
 */
export const PracticeFeedbackPanel = ({ 
  feedback, 
  practiceMode 
}) => {
  if (!feedback || !practiceMode) return null;

  return (
    <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/30">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-cyan-400 flex items-center gap-2">
          <Brain className="w-4 h-4" />
          Practice Feedback
        </h3>
        <span className={`text-2xl font-bold ${
          feedback.overall_score >= 70 ? "text-green-400" :
          feedback.overall_score >= 50 ? "text-yellow-400" :
          "text-red-400"
        }`}>
          {feedback.overall_score}/100
        </span>
      </div>
      
      {feedback.specific_feedback && (
        <p className="text-white/70 text-sm mb-3">{feedback.specific_feedback}</p>
      )}
      
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        {feedback.strengths?.length > 0 && (
          <div className="p-2 bg-green-500/10 rounded-lg">
            <h4 className="text-green-400 text-xs font-semibold mb-1">Strengths</h4>
            <ul className="text-white/60 text-xs space-y-1">
              {feedback.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-1">
                  <span className="text-green-400">+</span> {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {feedback.areas_to_improve?.length > 0 && (
          <div className="p-2 bg-orange-500/10 rounded-lg">
            <h4 className="text-orange-400 text-xs font-semibold mb-1">To Improve</h4>
            <ul className="text-white/60 text-xs space-y-1">
              {feedback.areas_to_improve.map((a, i) => (
                <li key={i} className="flex items-start gap-1">
                  <span className="text-orange-400">•</span> {a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {feedback.alternative_approaches?.length > 0 && (
        <div className="p-2 bg-blue-500/10 rounded-lg mb-2">
          <h4 className="text-blue-400 text-xs font-semibold mb-1">Try This Instead</h4>
          <ul className="text-white/60 text-xs space-y-1">
            {feedback.alternative_approaches.slice(0, 2).map((alt, i) => (
              <li key={i}>{alt}</li>
            ))}
          </ul>
        </div>
      )}
      
      {feedback.next_step_suggestion && (
        <p className="text-cyan-400 text-xs">
          <strong>Next:</strong> {feedback.next_step_suggestion}
        </p>
      )}
    </div>
  );
};

/**
 * Coach Annotations Display - Shows V2 coach feedback on messages
 */
export const CoachAnnotations = ({ 
  annotations, 
  showAnnotations,
  source = "v2"  // "v1" or "v2" to identify source
}) => {
  if (!annotations?.length || !showAnnotations) return null;

  return (
    <div className="mt-3 space-y-2">
      {annotations.map((annotation, i) => (
        <div key={i} className={`p-2 rounded-lg text-xs ${
          annotation.type === "positive" ? "bg-green-500/10 border border-green-500/20" :
          annotation.type === "opportunity" ? "bg-blue-500/10 border border-blue-500/20" :
          annotation.type === "caution" ? "bg-orange-500/10 border border-orange-500/20" :
          "bg-purple-500/10 border border-purple-500/20"
        }`}>
          <div className="flex items-start gap-2">
            <span className={`text-base ${
              annotation.type === "positive" ? "text-green-400" :
              annotation.type === "opportunity" ? "text-blue-400" :
              annotation.type === "caution" ? "text-orange-400" :
              "text-purple-400"
            }`}>
              {annotation.type === "positive" ? "✓" :
               annotation.type === "opportunity" ? "→" :
               annotation.type === "caution" ? "!" :
               "💡"}
            </span>
            <div className="flex-1">
              <p className="text-white/80">{annotation.observation}</p>
              {annotation.insight && (
                <p className="text-white/50 mt-0.5">{annotation.insight}</p>
              )}
              {annotation.suggestion && (
                <p className={`mt-1 font-medium ${
                  annotation.type === "positive" ? "text-green-400" :
                  annotation.type === "opportunity" ? "text-blue-400" :
                  annotation.type === "caution" ? "text-orange-400" :
                  "text-purple-400"
                }`}>
                  Tip: {annotation.suggestion}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
      {/* Source label - far right corner */}
      <div className="flex justify-end">
        <span className={`text-[10px] ${source === "v2" ? "text-cyan-400/60" : "text-purple-400/60"}`}>
          {source}
        </span>
      </div>
    </div>
  );
};

/**
 * Eros Tip Display - Shows coach tips in chat
 */
export const ErosTipBubble = ({ content }) => {
  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
        <div className="flex items-center gap-2 text-amber-400 text-xs font-medium mb-1">
          <span>💡</span>
          <span>Eros Tip</span>
        </div>
        <p className="text-white/80 text-sm">{content}</p>
      </div>
    </div>
  );
};

/**
 * Practice Feedback Bubble - Shows inline practice feedback
 */
export const PracticeFeedbackBubble = ({ message }) => {
  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-medium">
            <span>🎯</span>
            <span>Practice Feedback</span>
          </div>
          {message.score && (
            <span className="text-cyan-400 font-bold">{message.score}/10</span>
          )}
        </div>
        <p className="text-white/80 text-sm mb-2">{message.content}</p>
        {message.strengths && message.strengths.length > 0 && (
          <div className="mt-2">
            <span className="text-emerald-400 text-xs font-medium">✓ Strengths:</span>
            <ul className="text-white/70 text-xs ml-4 mt-1">
              {message.strengths.map((s, i) => <li key={i}>• {s}</li>)}
            </ul>
          </div>
        )}
        {message.improvements && message.improvements.length > 0 && (
          <div className="mt-2">
            <span className="text-amber-400 text-xs font-medium">↗ Improvements:</span>
            <ul className="text-white/70 text-xs ml-4 mt-1">
              {message.improvements.map((s, i) => <li key={i}>• {s}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Rollback Button - Shows rollback option for escalation mode messages
 */
export const RollbackButton = ({
  index,
  checkpoints,
  rollbackCredits,
  openRollbackModal
}) => {
  const handleRollback = () => {
    if (checkpoints.length === 0) {
      toast.error("No checkpoints yet - send more messages first");
      return;
    }
    
    const messageIndex = index + 1;
    const sortedCheckpoints = [...checkpoints].sort((a, b) => a.message_index - b.message_index);
    const checkpoint = sortedCheckpoints.find(cp => cp.message_index === messageIndex);
    
    if (checkpoint) {
      openRollbackModal(checkpoint);
    } else {
      const previousCheckpoints = sortedCheckpoints.filter(cp => cp.message_index < messageIndex);
      if (previousCheckpoints.length > 0) {
        openRollbackModal(previousCheckpoints[previousCheckpoints.length - 1]);
      } else {
        toast.error("No earlier checkpoint to rollback to");
      }
    }
  };

  return (
    <button
      onClick={handleRollback}
      disabled={rollbackCredits <= 0 || checkpoints.length === 0}
      className={`flex items-center gap-1 text-xs transition-colors ${
        rollbackCredits > 0 && checkpoints.length > 0
          ? "text-white/40 hover:text-white/70" 
          : "text-white/20 cursor-not-allowed"
      }`}
      title={
        rollbackCredits <= 0 ? "No rollback credits" :
        checkpoints.length === 0 ? "No checkpoints available" :
        "Rollback to this point"
      }
    >
      <RotateCcw className="w-3 h-3" />
      <span>Rollback({rollbackCredits})</span>
    </button>
  );
};

export default PracticeFeedbackPanel;
